import { PropType, defineComponent } from "vue";
import { Flow } from "@mv/flow-shared-utils/types/flow";
import { loadDefinitionDiagram, validateAssigneeInXML } from "@/store/flow/actions";
import { deploymentDefinition, loadDeploymentCategories, loadCategoriesByProcess } from "@/store/deploy/actions";
import sdkDotsLoading from "@/components/sdk-ui/sdk-dots-loading.vue";

type deployStatus = "default" | "error" | "empty";

export default defineComponent({
  name: "sdk-bpmn-action-deploy",
  components: {
    sdkDotsLoading,
  },
  props: {
    definition: {
      type: Object as PropType<Flow.Process.Definition.definition>,
      required: true,
    },
    definitionId: {
      type: String,
      required: true,
    },
    definitionKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      category: {} as any,
      categories: [] as any[],
      processCategory: [] as any[],
      status: "default" as deployStatus,
      loading: {
        categories: false,
      },
    };
  },
  computed: {
    emptyMessage(): string {
      if (this.status === "error") {
        return this.$tc("flow.deploy.error.categories");
      } else if (this.status === "empty") {
        return this.$tc("flow.deploy.empty.categories");
      } else {
        return "";
      }
    },
    showMessage(): boolean {
      return this.status !== "default";
    },
  },
  methods: {
    async loadFlowCategories(open: boolean) {
      if (open) {
        this.status = "default";
        this.loading.categories = true;
        await this.$dispatch(loadDeploymentCategories)([])
          .then((res: any) => {
            this.categories = res;
            if (!this.categories.length) {
              this.status = "empty";
            }
          })
          .catch(() => {
            this.status = "error";
          });
        this.processCategory = await this.$dispatch(loadCategoriesByProcess)([this.definition.key]).finally(() => {
          this.loading.categories = false;
        });
      } else {
        this.categories = [];
      }
    },
    async validateXML(): Promise<boolean> {
      const { bpmn20Xml } = await this.$dispatch(loadDefinitionDiagram)([{ id: this.definitionId, key: this.definitionKey }]);
      const isValid = await this.$dispatch(validateAssigneeInXML)([bpmn20Xml]);
      return isValid;
    },
    async definitionDeployment(category: any) {
      const xmlIsInValid = await this.validateXML();

      const dialogConfig: any = {
        title: `${this.$t("flow.deploy.deployTo")}<br>${category.name}?`,
        showCancelButton: true,
        confirmButtonText: this.$t("flow.deploy.buttons.confirmButton"),
        cancelButtonText: this.$t("flow.deploy.buttons.cancelButton"),
        showCloseButton: true,
        showLoaderOnConfirm: true,
        reverseButtons: true,
      };

      if (xmlIsInValid) {
        dialogConfig.text = this.$t("flow.deploy.unassignedTasksWarning");
        dialogConfig.customClass = { content: "xml-assignee-warning" };
        dialogConfig.icon = "warning";
      }

      const ask = await (this as any).$swal(dialogConfig);

      if (ask.value) {
        const lock = (this as any).$loading({
          lock: true,
          text: this.$t("flow.deploy.loading"),
          spinner: "el-icon-loading",
        });
        try {
          await this.$dispatch(deploymentDefinition)([category.id, this.definition.key, this.definition.name]);

          lock.close();

          const title = (this.$t("flow.deploy.success.title") as string) || "";
          const message = (this.$t("flow.deploy.success.message") as string) || "";

          this.$swal(title, message, "success");
          this.$emit("deploy-finised");
        } catch (err) {
          lock.close();
        }
      }
    },
  },
});
